import _ from 'lodash';
import Moment from 'moment';
import React from 'react';

import { DataTableProps } from './DataTable';
import { getDataTableColumns } from './DataTableMetadataHelpers';
import DataTableSortColumn from './DataTableSortColumn';

export type SortDirection = "ascending" | "descending";

export interface WithSortingState<V> {
    sortColumn?: string;
    sortDirection: SortDirection;
    sortedData?: V[];
}

export interface WithSortingProps {
    sortColumn?: string;
    sortDirection?: SortDirection;
    updateSort?: (sortColumn?: string, sortDirection?: string) => void;
}

export default <P extends DataTableProps<V>, V>(Component: React.ComponentType<P>) =>
    class WithSorting extends React.Component<P & WithSortingProps, WithSortingState<V>> {
        constructor(props: Readonly<P & WithSortingProps>) {
            super(props);

            this.state = {
                sortColumn: props.sortColumn,
                sortDirection: (props.sortDirection as SortDirection | undefined) || "ascending",
                sortedData: props.data
            };
        }

        componentDidMount() {
            this.setState({
                sortedData: this.getSortedData()
            });
        }

        componentDidUpdate(oldProps: P & WithSortingProps, oldState: WithSortingState<V>) {
            if (oldProps.sortColumn !== this.props.sortColumn || oldProps.sortDirection !== this.props.sortDirection) {
                this.setState({
                    sortColumn: this.props.sortColumn as string | undefined,
                    sortDirection: (this.props.sortDirection as SortDirection | undefined) || "ascending"
                });

                if (this.props.updateSort) {
                    this.setState({
                        sortedData: this.getSortedData()
                    })
                }
            }

            if (oldState.sortColumn !== this.state.sortColumn || oldState.sortDirection !== this.state.sortDirection || oldProps.data !== this.props.data) {
                if (this.props.updateSort && oldProps.data === this.props.data) {
                    this.props.updateSort(this.state.sortColumn, this.state.sortDirection);
                } else {
                    this.setState({
                        sortedData: this.getSortedData()
                    });
                }
            }
        }

        render() {
            const { children, data, sortColumn, sortDirection, updateSort, ...rest } = this.props;

            const columns = getDataTableColumns(children, data);

            return <Component data={this.state.sortedData} {...rest as any}>{columns && columns.map((column: any) => {
                if (!DataTableSortColumn.prototype.isPrototypeOf(column.type.prototype) && DataTableSortColumn.prototype !== column.type.prototype) {
                    return column;
                }

                const { heading, ...rest } = column.props;

                return <DataTableSortColumn<V, any> key={column.props.prop} heading={<button className="btn btn-link text-left b-0 p-0 align-baseline" style={{fontWeight: "bold"}} onClick={() => this.clickSortHeading(column.props.prop)}>{heading}{this.getSortIndicator(column.props.prop)}</button>} {...rest} />
            })}</Component>;
        }

        getCurrentColumnElement() {
            const sortColumn = this.state.sortColumn;

            if (!sortColumn) {
                return;
            }

            const columns = getDataTableColumns(this.props.children, this.props.data);

            if (!columns || !columns.length) {
                return;
            }

            const matches = columns.filter(x => (x.props as any).prop === sortColumn);

            if (matches.length) {
                return matches[0];
            }

            return;
        }

        getSortedData(): V[] | undefined {
            const sortColumn = this.state.sortColumn;

            if (!sortColumn) {
                return this.props.data;
            }

            const multiplier = this.state.sortDirection == "descending" ? -1 : 1;
            const result = _.clone(this.props.data);

            if (!result) {
                return;
            }

            const columnElement = this.getCurrentColumnElement() as any;

            let sort = columnElement?.props.sort || ((a: any, b: any) => {
                if (a === null || a === undefined) {
                    return -1;
                } else if (b === null || b === undefined) {
                    return 1;
                } else if (a > b) {
                    return 1;
                } else if (a < b) {
                    return -1;
                } else {
                    return 0;
                }
            });

            switch (columnElement?.props.sortType) {
                case "string":
                    sort = (a: any, b: any) => sort((a || "").toString(), (b || "").toString());
                    break;
                
                case "number":
                    sort = (a: any, b: any) => sort(new Number(a), new Number(b));
                    break;
                
                case "date":
                    sort = (a: any, b: any) => sort(Moment(a).toDate(), Moment(b).toDate());
                    break;
            }

            result.sort((a: any, b: any) => {
                return sort(a[sortColumn], b[sortColumn]) * multiplier;
            });

            return result;
        }

        clickSortHeading(sortColumn: string) {
            if (this.state.sortColumn != sortColumn) {
                this.setState({
                    sortColumn: sortColumn,
                    sortDirection: "ascending"
                });
            } else if (this.state.sortDirection == "ascending") {
                this.setState({
                    sortDirection: "descending"
                });
            } else {
                this.setState({
                    sortColumn: undefined
                });
            }
        }

        getSortIndicator(sortColumn: string): React.ReactNode {
            if (this.state.sortColumn != sortColumn) {
                return <React.Fragment></React.Fragment>;
            }

            if (this.state.sortDirection == "ascending") {
                return <i className="material-icons d-inline-block align-middle" style={{fontSize: "16px"}}>keyboard_arrow_up</i>;
            }

            return <i className="material-icons d-inline-block align-middle" style={{fontSize: "16px"}}>keyboard_arrow_down</i>;
        }
    }

export const validateSortDirection = (sortDirection?: string): SortDirection | undefined => {
    if (!sortDirection) {
        return;
    }

    switch (sortDirection) {
        case "ascending":
        case "descending":
            return sortDirection;
        default:
            throw new Error(`Sort direction ${sortDirection} is not a valid sort direction.`);
    }
};
